import React from 'react';
import { Link } from 'react-router-dom';
import { Library, Scale, Wrench, ExternalLink } from 'lucide-react';
import { PageHeader, Card, Table, useDialect } from './common';

/**
 * What to build a production card gateway on — and the licence question that
 * comes with the obvious answer, stated plainly so the choice is made
 * deliberately rather than discovered later.
 */
export default function ClientLibraries() {
  const { nm, defines } = useDialect();
  const echo = nm.codes?.echoTest ?? '?';
  return (
    <div className="space-y-6 max-w-4xl">
      <PageHeader icon={Library} title="Client Libraries">
        Building the gateway that will connect to a production card switch. The Sandbox does not require any particular library;
        this is AfricaNenda's recommendation, and why.
      </PageHeader>

      <Card title="Recommended: jPOS (Java)" icon={Library}>
        <p className="text-sm text-gray-700">
          jPOS is the most widely deployed open-source ISO 8583 framework. It handles what a production gateway must get right and
          a sandbox never shows you: dozens of vendor packagers (field definitions per switch), channel implementations for the
          common framings, a transaction manager, multiplexing of many requests over one link, and years of production use.
          If your team works in Java, starting from jPOS is almost always faster and safer than starting from nothing.
        </p>
        <a href="https://jpos.org" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline mt-3">
          jpos.org <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </Card>

      <Card title="Before you choose it: the licence" icon={Scale}>
        <p className="text-sm text-gray-700">
          jPOS is licensed under the <strong>GNU Affero General Public License v3 (AGPL-3.0)</strong>. Its section 13 extends the GPL's
          obligations to software used over a network: if you modify jPOS and let others interact with it over a network, you must
          offer those users the corresponding source code. A <strong>commercial licence</strong> is available from jPOS's maintainers
          for organisations that cannot accept those terms.
        </p>
        <p className="text-sm text-gray-700 mt-3">
          Whether AGPL obligations reach your gateway depends on how it is built and deployed — that is a question for your legal
          counsel, not for this page. Make the choice deliberately, before the gateway is in production.
        </p>
      </Card>

      <Card title="Whatever you build on, configure it for this switch" icon={Wrench}>
        <Table head={['Setting', 'This Sandbox']} rows={[
          ['Framing', '2-byte big-endian length, excluding itself (jPOS: a channel with a 2-byte binary header)'],
          ['MTI and numeric fields', 'ASCII'],
          ['Bitmaps', 'binary, 8 bytes each; the secondary present when bit 1 is set'],
          ['Variable-length fields', 'LLVAR / LLLVAR with ASCII length digits'],
          ['Field definitions', 'ISO 8583:1987 ASCII baseline — see Codes & Messages; a vendor profile for your target switch replaces it'],
          ['Correlation', defines(7) ? 'STAN (DE 11) + DE 7; responses echo both' : 'STAN (DE 11); responses echo it'],
          ['Keep-alive', `answer ${nm.requestMti} / DE ${nm.field} = ${echo} with ${nm.responseMti}; a silent link is closed`],
        ]} />
        <p className="text-xs text-gray-500 mt-3">
          To check your packager's output byte for byte, build the same message in the{' '}
          <Link to="/iso8583/message-generator" className="text-primary-600 hover:underline">Message Generator</Link> — the
          Sandbox assembles it with the switch's own codec — or paste your gateway's frame into{' '}
          <Link to="/iso8583/explain" className="text-primary-600 hover:underline">Explain a Frame</Link> and read back what
          the switch makes of it. <Link to="/iso8583/getting-started" className="text-primary-600 hover:underline">Getting
          Started</Link> has a complete sign-on in about thirty lines of Python, if you would rather start from nothing.
        </p>
      </Card>
    </div>
  );
}
